import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { opsApi } from '@/api/client';

/**
 * Operations — CDN onboarding gaps (fleet). Served by @mgmt `/internal/ops/cdn/*`.
 * Auth: UMA RPT (opsApi client) — employee-only; attach additionally Tech-gated.
 *
 * API contract (BE: modules/service/ops/cdn_onboarding_gaps.go):
 *   GET  /internal/ops/cdn/onboarding-gaps[?provider=eagle|raven|hawk]  → OpsOnboardingGap[]
 *   POST /internal/ops/cdn/onboarding-gaps/attach {domain, service_id}  → OpsAttachResult
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

/** Suggested next action the operator should take for a gap. */
export type GapAction = 'attach' | 'create-secondary' | 'needs-service';

/** How confidently the org/service was resolved. */
export type GapConfidence = 'entity' | 'existing-binding' | 'none';

/** One unresolved CDN onboarding gap, enriched with a resolution suggestion. */
export interface OpsOnboardingGap {
  provider: string;
  domain: string;
  entity_id: string;
  volume_bytes: number;
  organization_id: string;
  suggested_service_id: string;
  suggested_service_name: string;
  existing_bindings: string;
  suggested_action: GapAction;
  confidence: GapConfidence;
  first_seen_at: string;
  last_seen_at: string;
}

/** Result of a successful attach. */
export interface OpsAttachResult {
  domain: string;
  domain_id: string;
  binding_id: string;
  service_id: string;
  service_name: string;
  provider: string;
  organization_id: string;
  already_bound: boolean;
}

export interface AttachGapRequest {
  domain: string;
  service_id: string;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const opsCdnGapKeys = {
  all: ['ops', 'cdn-gaps'] as const,
  list: (provider?: string) => ['ops', 'cdn-gaps', provider ?? 'all'] as const,
};

/* ── Hooks ─────────────────────────────────────────────────────────────── */

/** GET /internal/ops/cdn/onboarding-gaps — fleet list of unresolved gaps. */
export function useOpsOnboardingGaps(provider?: string) {
  return useQuery({
    queryKey: opsCdnGapKeys.list(provider),
    queryFn: async (): Promise<OpsOnboardingGap[]> => {
      const query = provider ? `?${qs.stringify({ provider })}` : '';
      const res = await opsApi.get<{ result: OpsOnboardingGap[] }>(
        `/internal/ops/cdn/onboarding-gaps${query}`,
      );
      return res.data.result ?? [];
    },
  });
}

/** POST /internal/ops/cdn/onboarding-gaps/attach — bind-only attach. */
export function useAttachOnboardingGap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AttachGapRequest): Promise<OpsAttachResult> => {
      const res = await opsApi.post<{ result: OpsAttachResult }>(
        '/internal/ops/cdn/onboarding-gaps/attach',
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opsCdnGapKeys.all });
    },
  });
}

/* ── Resolve owning org (sibling-root hint) ────────────────────────────── */

/** A CDN service the candidate org already has. */
export interface ResolveOrgService {
  service_id: string;
  service_name: string;
  provider: string;
  active_bindings: number;
  has_credential: boolean;
}

/** One inferred owner org + the sibling-domain evidence it was inferred from. */
export interface ResolveOrgCandidate {
  organization_id: string;
  sibling_domains: number;
  sample_siblings: string[];
  cdn_services: ResolveOrgService[];
}

/** Authoritative owner lookup from the vendor itself (vEdge/raven ListDomains). */
export interface ProviderMatch {
  provider: string;
  found: boolean;
  vendor_org_id: string;
  vendor_org_name: string;
  organization_id: string; // parsed from vendor_org_name ("" when not "<id> - <name>")
  status: string;
}

export interface ResolveDomainOrgResult {
  domain: string;
  root: string;
  candidates: ResolveOrgCandidate[];
  provider_matches: ProviderMatch[];
}

/* ── Create-secondary: provision the missing provider service + attach ─────── */

export interface CreateSecondaryServiceRequest {
  domain: string;
  organization_id: string;
  provider: string;
  service_name?: string;
  vendor_uid?: string;
}

export interface CreateSecondaryServiceResult {
  service_id: string;
  service_zone_id: string;
  binding_id: string;
  domain_id: string;
  provider: string;
  organization_id: string;
  parent_service_id: string;
}

/**
 * POST /internal/ops/cdn/onboarding-gaps/create-service — for a create-secondary
 * gap: create the missing CDN provider service (+ minimal service_zone), link it
 * under the org's CDN family, and bind the already-serving domain. Bind-only; no
 * vendor entity is created.
 */
export function useCreateSecondaryService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSecondaryServiceRequest): Promise<CreateSecondaryServiceResult> => {
      const res = await opsApi.post<{ result: CreateSecondaryServiceResult }>(
        '/internal/ops/cdn/onboarding-gaps/create-service',
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opsCdnGapKeys.all });
    },
  });
}

/**
 * GET /internal/ops/cdn/resolve-org — infer the owning org of a domain from
 * sibling domains sharing the same registrable root. Best-effort hint only;
 * the operator still verifies the customer in DataCentral. Returns an empty
 * candidate list when there is no sibling match (new customer).
 */
export async function resolveDomainOrg(domain: string): Promise<ResolveDomainOrgResult> {
  const res = await opsApi.get<{ result: ResolveDomainOrgResult }>(
    `/internal/ops/cdn/resolve-org?${qs.stringify({ domain })}`,
  );
  return res.data.result;
}
